import type React from 'react'
import type { IconName, PhosphorIconName } from '@/components/atomic/icon'

import Text, { FontWeight, TextVariant } from '@/components/atomic/text'
import Toggle from '@/components/atomic/toggle'
import { IconBlock } from '@/components/molecules/icon-block'

interface ModelSectionCardContainerProps {
  children: React.ReactNode
}

interface ModelSectionCardHeaderProps {
  icon?: IconName | PhosphorIconName
  isPhosphor?: boolean
  title: string
  description: string
  iconColor?: string
  iconBackgroundColor?: string
  headerContent?: React.ReactNode
  required?: boolean
  toggleable?: boolean
  isToggleChecked?: boolean
  onToggleChange?: (checked: boolean) => void
}

export function ModelSectionCardContainer({
  children,
}: ModelSectionCardContainerProps) {
  return (
    <div className="w-full px-4 pt-3 pb-4 border border-primary-border rounded-md min-w-[492px] bg-background">
      {children}
    </div>
  )
}

export function ModelSectionCardHeader({
  icon,
  isPhosphor,
  title,
  description,
  iconColor,
  iconBackgroundColor,
  headerContent,
  required = false,
  toggleable = false,
  isToggleChecked,
  onToggleChange,
}: ModelSectionCardHeaderProps) {
  return (
    <div className="flex justify-between items-center gap-4 w-full">
      <div
        className={`flex justify-start items-center gap-2 ${headerContent ? 'w-1/2' : 'w-full'}`}
      >
        {icon && (
          <IconBlock
            icon={icon}
            className={iconBackgroundColor}
            textColor={iconColor}
            isPhosphor={isPhosphor}
          />
        )}
        <div className="flex flex-col w-full">
          <div className="flex items-center gap-0.5">
            <Text
              variant={TextVariant.BodySmall}
              fontWeight={FontWeight.Medium}
            >
              {title}
            </Text>
            {required && (
              <Text
                variant={TextVariant.BodySmall}
                className="text-moment-red-500"
              >
                *
              </Text>
            )}
          </div>
          <Text variant={TextVariant.BodySubtext} className="text-secondary">
            {description}
          </Text>
        </div>
        {toggleable && (
          <Toggle
            checked={isToggleChecked}
            onChange={(checked) => onToggleChange?.(checked)}
          />
        )}
      </div>
      {headerContent && (
        <div className="w-1/2 flex justify-end">{headerContent}</div>
      )}
    </div>
  )
}

export function ModelSectionCardContent({
  children,
}: {
  children: React.ReactNode
}) {
  return <div className="mt-4">{children}</div>
}
